import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useSession } from '../SessionProvider';
import {
  getOverlayAISWeb,
  getOverlayMetadata,
  getOverlayMP,
  GetOverlayProps,
} from './getOverlay';
import { useRef, useState } from 'react';
import { Base64String } from '../utils';
import { useTimeZoneContext } from '@ais-ui/utils';

export const useGetOverlayAISWeb = (
  props: GetOverlayProps | null
): UseQueryResult<Base64String, unknown> => {
  const { olSession } = useSession();

  const [refetchInterval, setRefetchInterval] = useState<number>(5000);
  const lastRequestTimeRef = useRef<number>(Date.now());
  const shouldFetch = () => {
    const now = Date.now();
    return now - lastRequestTimeRef.current >= 10; // Ensure at least 10ms between requests
  };
  const enabled = props !== null && shouldFetch();

  return useQuery(
    [
      'getOverlay',
      props?.centerLon,
      props?.centerLat,
      props?.zoomLevel,
      props?.height,
      props?.width,
    ],
    () => {
      if (props === null) {
        throw new Error('Props cannot be null in useGetOverlay');
      }
      return getOverlayAISWeb(olSession, props);
    },
    {
      enabled: enabled,
      cacheTime: 10,
      refetchInterval: refetchInterval,
      onSuccess: (data) => {
        getOverlayMetadata(olSession).then((metadata) => {
          const newRefetchInterval = metadata.timeUntilNextUpdate;
          setRefetchInterval(newRefetchInterval);
        });
      },
    }
  );
};

export const useGetOverlayMP = (
  props: GetOverlayProps | null
): UseQueryResult<
  { overlay: Base64String; overlayCreatedDatetime: string },
  unknown
> => {
  const { olSession } = useSession();
  const { timeZone } = useTimeZoneContext();
  const [updateInterval, setUpdateInterval] = useState(5000);

  return useQuery(
    [
      'getOverlay',
      props?.centerLon,
      props?.centerLat,
      props?.zoomLevel,
      props?.height,
      props?.width,
      timeZone,
    ],
    async () => {
      const result = await getOverlayMP(olSession, props!, timeZone);
      const metadata = await getOverlayMetadata(olSession);
      setUpdateInterval(metadata.timeUntilNextUpdate);
      return result;
    },
    {
      enabled: props !== null,
      refetchInterval: updateInterval,
      cacheTime: 10,
      retry: 3,
      retryDelay: 1000,
    }
  );
};
